import axios from 'axios';
import { CONVERTER_SINGLE_API_BASE } from './config';
import {
  API_VARIANT_MINEX,
  API_VARIANT_ISO,
  API_VARIANT_RAW,
  buildBiosdkOperationUrl,
  normalizeApiVariant,
} from './biosdkRoutes';
import { extractMinexFingerTemplate } from './minexFingerMatch';

export const SLAP_STEPS = [
  {
    id: 'left',
    slapType: 'LEFT_SLAP',
    label: 'Left slap (4 fingers)',
    shortLabel: 'Left slap',
    hint: 'Place left index, middle, ring, and little fingers flat. Thumbs excluded.',
  },
  {
    id: 'right',
    slapType: 'RIGHT_SLAP',
    label: 'Right slap (4 fingers)',
    shortLabel: 'Right slap',
    hint: 'Place right index, middle, ring, and little fingers flat. Thumbs excluded.',
  },
  {
    id: 'thumbs',
    slapType: 'BOTH_THUMBS',
    label: 'Both thumbs (2 fingers)',
    shortLabel: 'Thumbs',
    hint: 'Place left and right thumbs side by side.',
  },
];

const BIO_SUBTYPE_TO_MINEX_POSITION = {
  RIGHT_THUMB: 1,
  RIGHT_INDEX: 2,
  RIGHT_MIDDLE: 3,
  RIGHT_RING: 4,
  RIGHT_LITTLE: 5,
  LEFT_THUMB: 6,
  LEFT_INDEX: 7,
  LEFT_MIDDLE: 8,
  LEFT_RING: 9,
  LEFT_LITTLE: 10,
};

const BIO_SUBTYPE_TO_CONVERTER_SUBTYPE = {
  RIGHT_THUMB: 'RIGHT_THUMB',
  RIGHT_INDEX: 'RIGHT_INDEX_FINGER',
  RIGHT_MIDDLE: 'RIGHT_MIDDLE_FINGER',
  RIGHT_RING: 'RIGHT_RING_FINGER',
  RIGHT_LITTLE: 'RIGHT_LITTLE_FINGER',
  LEFT_THUMB: 'LEFT_THUMB',
  LEFT_INDEX: 'LEFT_INDEX_FINGER',
  LEFT_MIDDLE: 'LEFT_MIDDLE_FINGER',
  LEFT_RING: 'LEFT_RING_FINGER',
  LEFT_LITTLE: 'LEFT_LITTLE_FINGER',
};

export function bioSubTypeToConverterSubType(bioSubType) {
  const key = String(bioSubType || '').trim().toUpperCase();
  return BIO_SUBTYPE_TO_CONVERTER_SUBTYPE[key] || 'UNKNOWN';
}

export function extractApiError(err) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.error) return String(data.error);
  if (Array.isArray(data?.errors) && data.errors.length) {
    const first = data.errors[0];
    if (typeof first === 'string') return first;
    if (first?.message) return String(first.message);
  }
  return err?.message || 'Request failed';
}

export async function upscaleSegmentPngIfSmall(pngBytes, minWidth = 200, minHeight = 200) {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return pngBytes;
  }
  let url;
  try {
    const blob = new Blob([pngBytes], { type: 'image/png' });
    url = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to decode segment PNG'));
      el.src = url;
    });
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    const scale = Math.max(minWidth / Math.max(width, 1), minHeight / Math.max(height, 1), 1);
    if (scale <= 1) return pngBytes;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(minWidth, Math.round(width * scale));
    canvas.height = Math.max(minHeight, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return pngBytes;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const outBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!outBlob) return pngBytes;
    return new Uint8Array(await outBlob.arrayBuffer());
  } catch {
    return pngBytes;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function buildSegmentPreviewUrl(encodedImage) {
  if (!encodedImage) return null;
  return `data:image/png;base64,${encodedImage}`;
}

export function downloadBlob(filename, bytes) {
  const blob = bytes instanceof Blob ? bytes : new Blob([bytes]);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function signedBytesToUint8Array(signed) {
  return new Uint8Array((signed || []).map((b) => (b < 0 ? b + 256 : b)));
}

export function templateExtension(apiVariant) {
  const v = normalizeApiVariant(apiVariant);
  if (v === API_VARIANT_MINEX) return 'bin';
  if (v === API_VARIANT_ISO || v === API_VARIANT_RAW) return 'iso';
  return 'iso';
}

export async function segmentSlap(biosdkBaseUrl, encodedImage, slapType) {
  const url = `${biosdkBaseUrl.replace(/\/$/, '')}/segment-slap`;
  const response = await axios.post(url, { encodedImage, slapType }, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

async function convertFingerPngToIso(pngFile, bioSubType) {
  const mapped = bioSubTypeToConverterSubType(bioSubType);
  const candidateUrls = [
    `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=${mapped}`,
    `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN&purpose=AUTH`,
    `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN`,
  ];
  let lastError = null;
  for (const url of candidateUrls) {
    const formData = new FormData();
    formData.append('file', pngFile);
    try {
      const response = await axios.post(url, formData, { responseType: 'arraybuffer' });
      return response.data;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('PNG to ISO conversion failed');
}

function toSignedByteJson(arrayBuffer) {
  const unsigned = new Uint8Array(arrayBuffer);
  return Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
}

async function extractMosipStyleTemplate(biosdkBaseUrl, pngFile, bioSubType, apiVariant) {
  const isoBuffer = await convertFingerPngToIso(pngFile, bioSubType);
  const bdb = toSignedByteJson(isoBuffer);
  const inner = JSON.stringify({
    sample: {
      segments: [{ bdb, others: { bioSubType } }],
      others: {},
    },
  });
  const outer = {
    request: btoa(inner),
    operation: 'extract-template',
  };
  const isRaw = normalizeApiVariant(apiVariant) === API_VARIANT_RAW;
  const extractUrl = buildBiosdkOperationUrl(biosdkBaseUrl, 'extract-template', isRaw, apiVariant);
  const response = await axios.post(extractUrl, outer, {
    headers: { 'Content-Type': 'application/json' },
  });
  const segments = response.data?.response?.response?.segments;
  const bdbOut = segments?.[0]?.bdb;
  if (!bdbOut) {
    throw new Error(`No template in extract response for ${bioSubType}`);
  }
  return {
    template: bdbOut,
    templateLength: bdbOut.length,
    format: normalizeApiVariant(apiVariant) === API_VARIANT_ISO ? 'ISO19794' : 'BioSDK',
  };
}

export async function extractSegmentTemplate(biosdkBaseUrl, segment, apiVariant) {
  const bioSubType = segment.bioSubType || 'UNKNOWN';
  let pngBytes = base64ToUint8Array(segment.encodedImage);
  pngBytes = await upscaleSegmentPngIfSmall(pngBytes);
  const pngFile = new File([pngBytes], `${bioSubType}.png`, { type: 'image/png' });

  if (normalizeApiVariant(apiVariant) === API_VARIANT_MINEX) {
    const fingerPosition = BIO_SUBTYPE_TO_MINEX_POSITION[bioSubType] ?? 0;
    return extractMinexFingerTemplate(biosdkBaseUrl, pngFile, { fingerPosition });
  }
  return extractMosipStyleTemplate(biosdkBaseUrl, pngFile, bioSubType, apiVariant);
}

/** Steps that have an uploaded file (skipped / empty steps are omitted). */
export function listProvidedSlapSteps(filesByStepId) {
  return SLAP_STEPS.filter((step) => Boolean(filesByStepId[step.id]));
}

/** Segment-only: no extract; returns composite previews + bboxes per uploaded slap step. */
export async function runSegmentOnly(biosdkBaseUrl, filesByStepId, onProgress) {
  const steps = listProvidedSlapSteps(filesByStepId);
  if (steps.length === 0) {
    throw new Error('Upload at least one slap image (other steps may be skipped).');
  }
  const results = [];
  for (const step of steps) {
    const file = filesByStepId[step.id];
    onProgress?.({ phase: 'segment', step: step.id });
    const encodedImage = await fileToBase64(file);
    const segmentResponse = await segmentSlap(biosdkBaseUrl, encodedImage, step.slapType);
    const compositeUrl = URL.createObjectURL(file);
    results.push({
      slapType: step.slapType,
      slapStepId: step.id,
      label: step.label,
      compositeUrl,
      segments: segmentResponse.segments || [],
      segmentCount: segmentResponse.segmentCount,
    });
  }
  return results;
}

export async function runSlapLab(biosdkBaseUrl, filesByStepId, apiVariant, onProgress) {
  const steps = listProvidedSlapSteps(filesByStepId);
  if (steps.length === 0) {
    throw new Error('Upload at least one slap image (other steps may be skipped).');
  }
  const results = [];
  for (const step of steps) {
    const file = filesByStepId[step.id];
    onProgress?.({ phase: 'segment', step: step.id });
    const encodedImage = await fileToBase64(file);
    const segmentResponse = await segmentSlap(biosdkBaseUrl, encodedImage, step.slapType);
    const segments = segmentResponse.segments || [];
    for (const segment of segments) {
      onProgress?.({ phase: 'extract', step: step.id, bioSubType: segment.bioSubType });
      let extractResult = null;
      let extractError = null;
      try {
        extractResult = await extractSegmentTemplate(biosdkBaseUrl, segment, apiVariant);
      } catch (err) {
        extractError = extractApiError(err);
      }
      results.push({
        slapType: step.slapType,
        slapStepId: step.id,
        segment,
        previewUrl: buildSegmentPreviewUrl(segment.encodedImage),
        extractResult,
        extractError,
        bioSubType: segment.bioSubType,
      });
    }
  }
  return results;
}

export function downloadAllArtifacts(results, apiVariant, kind = 'all') {
  const ext = templateExtension(apiVariant);
  results.forEach((row) => {
    const subtype = row.bioSubType || 'finger';
    if ((kind === 'all' || kind === 'segments') && row.segment?.encodedImage) {
      downloadBlob(`${subtype}.png`, base64ToUint8Array(row.segment.encodedImage));
    }
    if ((kind === 'all' || kind === 'templates') && row.extractResult?.template) {
      downloadBlob(`${subtype}.${ext}`, signedBytesToUint8Array(row.extractResult.template));
    }
  });
}

export function buildManifest(results, apiVariant) {
  return {
    generatedAt: new Date().toISOString(),
    apiVariant: normalizeApiVariant(apiVariant),
    fingers: results.map((r) => ({
      bioSubType: r.bioSubType,
      slapType: r.slapType,
      confidence: r.segment?.confidence,
      templateLength: r.extractResult?.templateLength,
      format: r.extractResult?.format,
      status: r.extractError ? 'failed' : 'ok',
      error: r.extractError || null,
    })),
  };
}

export function downloadManifest(results, apiVariant) {
  const json = JSON.stringify(buildManifest(results, apiVariant), null, 2);
  downloadBlob('slap-lab-manifest.json', new Blob([json], { type: 'application/json' }));
}
